import { useReducer, FormEvent } from "react";
import { styled } from "@stitches/react";
import { DateTime } from "luxon";
import { useAuthStatusChange } from "@/hooks/use-notes";
import { AuthStatus, AuthStatusFormFields, Order } from "@/types/my-tasks";
import {
  Button,
  Form,
  FormField,
  Textarea,
  Input,
  DateInput,
  RadioGroup,
  FormFieldLayout,
  DateInputOnChangeEvent,
} from "@/vendor/Forge/dist";
import { useAppState } from "@contexts/app-state";
import { capitalize } from "@/utils/helpers";
import  { OrdersQueryKey } from "@/types/order-table";


const Wrapper = styled("div", {
  padding: "0 4px",
});

const CTAs = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  marginTop: 28,
});

const DateWrapper = styled("div", {
  display: "flex",
  gap: 16,

  "& > div:first-child": {
    width: 220,
  },
});

const closingReasons = [
  "Authorization is denied",
  "Order is returned to provider",
  "Patient declined to complete services",
  "Other",
];

const isAdditionaCustomActionNoteRequired = (
  formValues: AuthStatusFormFields,
  newStatus: AuthStatus,
) => {
  if (newStatus === "Closed") {
    return (
      !!formValues.closingReason &&
      formValues.closingReason !== "Patient declined to complete services"
    );
  }

  return false;
};

export const isValid = (
  formValues: AuthStatusFormFields,
  newStatus: AuthStatus,
) => {
  if (newStatus === "Closed") {
    if (!formValues.closingReason) return false;
    if (isAdditionaCustomActionNoteRequired(formValues, newStatus))
      return !!formValues.notes;
  }

  if (newStatus === "Obtained") {
    return !!(
      formValues.authorizationNumber &&
      formValues.beginningDate &&
      formValues.expiryDate
    );
  }

  return true;
};

interface NotesMapping {
  newStatus: AuthStatus;
  notes: string;
  authorizationNumber?: string;
  beginningDate: Date | null;
  expiryDate: Date | null;
  closingReason?: string;
  loggedInUser: string;
}

const toDateString = (date: Date) =>
  DateTime.fromJSDate(date).toFormat("MM/dd/yyyy");

export interface Props {
  ordersQueryKey: OrdersQueryKey;
  order: Order;
  newStatus: AuthStatus;
  onSubmit: () => void;
  onCancelClick: () => void;
  documentIds: string[];
}

const mapNotes = ({
  newStatus,
  notes,
  authorizationNumber,
  beginningDate,
  expiryDate,
  closingReason,
  loggedInUser,
}: NotesMapping) => {
  let actionNote = `${capitalize(
    loggedInUser,
  )} changed authorization status to ${newStatus}.`;
  const postFix = notes ? ` Additional Notes: ${notes}` : "";

  switch (newStatus) {
    case "Obtained":
      actionNote +=
        authorizationNumber && beginningDate && expiryDate
          ? ` Auth: ${authorizationNumber} valid ${toDateString(
              beginningDate,
            )}-${toDateString(expiryDate)}.`
          : "";
      break;
    case "Closed":
      actionNote += ` Closing Reason: ${closingReason ?? "Other"}.`;
  }

  return `${actionNote}${postFix}`;
};

const AuthStatusForm = ({
  ordersQueryKey,
  order,
  newStatus,
  onSubmit,
  onCancelClick,
  documentIds
}: Props) => {
  const { isPending: isCreatingNotes, changeAuthStatus } = useAuthStatusChange(
    { ordersQueryKey }
  );

  const { loggedInUser } = useAppState();

  const [formValues, setFormValues] = useReducer(
    (state: AuthStatusFormFields, changes: Partial<AuthStatusFormFields>) => ({
      ...state,
      ...changes,
    }),
    {
      notes: "",
      authorizationNumber: "",
      beginningDate: null,
      expiryDate: null,
      closingReason: "",
    },
  );
//  const {useInvalidateQueries} = useInvalidateQueriesMutation({ordersQueryKey: OrderTableQueryKey});

  const handleSubmit = (e: FormEvent<HTMLElement>) => {
    e.preventDefault();

    if (documentIds.length > 1) {
      changeAuthStatus({
        orderIds: documentIds,
        actionNote: mapNotes({ newStatus, ...formValues, loggedInUser }),
        changedField: "AUTH_STATUS",
        changedValue: newStatus,
      });
    } else {
      changeAuthStatus({
        orderIds: [order.documentId],
        actionNote: mapNotes({ newStatus, ...formValues, loggedInUser }),
        changedField: "AUTH_STATUS",
        changedValue: newStatus,
      });
    }



    onSubmit();
  };

  return (
    <Wrapper>
      <Form
        noValidate
        includeSubmitButton={false}
        labelAlwaysAbove
        labelWidth={12}
        buttonText="Change Auth Status"
        onSubmit={handleSubmit}
      >
      {documentIds.length > 1
        ?
          <p>
            You are changing the Auth Status for { documentIds.length } orders. Action notes created here will be attached to all selected orders.
          </p>
        :
          <p>
            Please complete for the order {`"${order.orderName}"`} for{" "}
            {order.patientName}...
          </p>}
        {newStatus === "Obtained" && (
          <>
            <FormField
              required
              id="authorization-number"
              labelText="Authorization Number"
              name="authorizationNumber"
              placeholder="11111111"
              value={formValues.authorizationNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ authorizationNumber: e.target.value })
              }
              inputAs={Input}
            />
            <DateWrapper>
              <FormField
                required
                id="beginning-date"
                labelText="Beginning Date"
                name="beginningDate"
                placeholder="MM-DD-YYYY"
                value={formValues.beginningDate}
                onChange={(e: DateInputOnChangeEvent) =>
                  setFormValues({ beginningDate: e.target.value })
                }
                inputAs={DateInput}
              />
              <FormField
                required
                id="expiry-date"
                labelText="Expiry Date"
                name="expiryDate"
                placeholder="MM-DD-YYYY"
                value={formValues.expiryDate}
                onChange={(e: DateInputOnChangeEvent) =>
                  setFormValues({ expiryDate: e.target.value })
                }
                inputAs={DateInput}
              />
            </DateWrapper>
          </>
        )}
        {newStatus === "Closed" && (
          <>
            <FormField
              required
              id="reason-for-closing"
              labelText="Reason for Closing"
              name="closingReason"
              options={closingReasons}
              value={formValues.closingReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ closingReason: e.target.value })
              }
              inputAs={RadioGroup}
            />
          </>
        )}
        <FormField
          required={isAdditionaCustomActionNoteRequired(formValues, newStatus)}
          id="custom-action-note"
          name="notes"
          labelText="Additional Custom Action Note"
          value={formValues.notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues({ notes: e.target.value })
          }
          inputAs={Textarea}
        />
        <FormFieldLayout
          inputSlot={
            <CTAs>
              <Button
                fullWidth
                type="submit"
                disabled={!isValid(formValues, newStatus) || isCreatingNotes}
              >
                {isCreatingNotes ? "Updating Status..." : "Change Auth Status"}
              </Button>
              <Button
                fullWidth
                type="button"
                variant="secondary"
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            </CTAs>
          }
        />
      </Form>
    </Wrapper>
  );
};

export default AuthStatusForm;
